import React, { useState } from 'react';
import { Link as LinkIcon, Copy, ExternalLink, CheckCircle2 } from 'lucide-react';
import HelpIconButton from '../HelpIconButton';

// Flat verification URL list with copy-to-clipboard + open-in-new-tab
// actions. Rows are grouped by vendor. Placeholder data until the
// canonical list lands.

interface ReferenceLink {
  vendor: string;
  label: string;
  url: string;
  lastVerified: string;
}

const LINKS: ReferenceLink[] = [
  {
    vendor: 'TSG',
    label: 'Public Trust Page',
    url: 'https://tsg.example.com/trust',
    lastVerified: '2026-03-15',
  },
  {
    vendor: 'Railway',
    label: 'SOC 2 Type II (Aug 2025)',
    url: 'https://railway.app/trust',
    lastVerified: '2026-03-15',
  },
  {
    vendor: 'Anthropic',
    label: 'Trust Center',
    url: 'https://trust.anthropic.com',
    lastVerified: '2026-03-15',
  },
  {
    vendor: 'OpenAI',
    label: 'Security & Compliance',
    url: 'https://openai.com/security',
    lastVerified: '2026-03-15',
  },
];

const TrustPortalQuickReference: React.FC = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard API unavailable — silently ignore. User can still click
      // the external link button.
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-cyan-100 rounded-lg">
            <LinkIcon className="w-6 h-6 text-cyan-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quick Reference</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Independent verification URLs for TSG and its sub-providers.
            </p>
          </div>
        </div>
        <HelpIconButton screenId="trust-portal-quick-reference" showLabel />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <div className="col-span-3">Vendor</div>
          <div className="col-span-4">Label</div>
          <div className="col-span-3">URL</div>
          <div className="col-span-2 text-right">Last verified</div>
        </div>
        {LINKS.map((row, idx) => (
          <div
            key={`${row.vendor}-${row.label}`}
            className={`grid grid-cols-12 items-center px-4 py-3 text-sm ${
              idx !== LINKS.length - 1 ? 'border-b border-gray-100' : ''
            }`}
          >
            <div className="col-span-3 font-semibold text-gray-900">{row.vendor}</div>
            <div className="col-span-4 text-gray-700">{row.label}</div>
            <div className="col-span-3 flex items-center gap-2 min-w-0">
              <span className="truncate text-gray-500 text-xs font-mono">{row.url}</span>
              <button
                onClick={() => handleCopy(row.url)}
                className="p-1 text-gray-400 hover:text-blue-700 transition-colors"
                title="Copy URL"
              >
                {copied === row.url ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
              <a
                href={row.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 text-gray-400 hover:text-blue-700 transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="col-span-2 text-right text-xs text-gray-500">
              {row.lastVerified}
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500">
        Placeholder URL list — will be replaced with the canonical Quick Reference content
        when Ryan&apos;s handoff doc lands.
      </div>
    </div>
  );
};

export default TrustPortalQuickReference;
